import React, { SyntheticEvent, useEffect } from 'react'
import classNames from 'classnames'
import { Trans, t } from '@lingui/macro'

import Input from '../../../../components/common/form/input/Input'
import IconPicture from '../../../../components/common/icon/IconPicture'
import EditorQuill from '../../../../components/common/quill/EditorQuill'
import { LooopS3FileUpload } from '../../../../app/util/LooopS3FileUpload'

import { LooopS3BucketEnum } from '../../../../../../types/UploadCredentials'
import { replaceBase64ImagesInHtml } from '../../../topic-detail-page/components/utils'
import ButtonGroup from '../../../../components/common/form/button/ButtonGroup'
import Label from '../../../../components/common/form/label/Label'
import {
  ButtonPrimary,
  ButtonDanger,
  ButtonLight
} from 'front-end/src/components'

import uniqueId from '../../../../components/utils/uniqueId'
import {
  ICampaignEmailComponentProps,
  ICampaignEmailComponentState,
  useIsManagerNotificationPath
} from './CampaignEmailComponent'
import validations from '../../../../../../shared/validations'
import NewActionHeader from '../new-action-header/NewActionHeader'
import IconArrowRight from '../../../../components/common/icon/IconArrowRight'
import { addInlineWidthAttributeToImages } from '../../../../../../shared/htmlProcessing'
import { CampaignActionEditingSteps } from '../../common'
import { TemplatingInfoBar } from '../TemplatingInfoBar'

import { Box } from '@chakra-ui/react'
import { SenderSelect } from '../SenderSelect'
import { brandColors } from 'shared/basicPalette'

import { TooltipHelpQuestion } from '../../../../components/common/tooltip/TooltipHelp'

export const CampaignEmailStepCreate = (
  props: ICampaignEmailComponentState & {
    onStateUpdate: (updatedState: Partial<ICampaignEmailComponentState>) => void
  } & ICampaignEmailComponentProps
) => {
  const emailSubjectId: string = uniqueId()
  const emailBodyId: string = uniqueId()
  const buttonLinkId: string = uniqueId()
  const buttonTextId: string = uniqueId()

  const isManagerNotification =
    useIsManagerNotificationPath() || props.is_for_manager

  let inputFileRef: HTMLInputElement | null = null

  useEffect(() => {
    if (props.coverImageFile) {
      onCoverImage(props.coverImageFile)
    }
  }, [props.coverImageFile])

  const onCoverImage = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => {
      // @ts-expect-error
      props.onStateUpdate({ coverImageBase64: reader.result })
    }
    reader.readAsDataURL(file)
  }

  const onImageChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files
    if (files?.length) {
      const file = files[0]
      if (file.type.match('image.*')) {
        props.onStateUpdate({ coverImageFile: file, coverImageSrc: null })
      } else {
        props.onStateUpdate({ coverImageFile: null })
      }
    }
  }

  // TODO: just drill the preview prop?
  const previewEnabled = (): boolean => {
    const { buttonLinkValid, emailSubject, emailBody, buttonLink, buttonText } =
      props

    if (buttonLink || buttonText) {
      return !!(
        buttonLinkValid &&
        emailSubject &&
        emailBody &&
        buttonLink &&
        buttonText
      )
    } else {
      return !!(emailSubject && emailBody)
    }
  }

  return (
    <div className="CampaignEmailComponent CampaignEmailComponent__create">
      <NewActionHeader
        title={isManagerNotification ? t`Notify manager` : t`Send email`}
        steps={CampaignActionEditingSteps}
        currentStep={CampaignActionEditingSteps.CREATE}
        nextButton={
          <ButtonPrimary
            onClick={() => {
              props.onStateUpdate({
                step: CampaignActionEditingSteps.PREVIEW
              })
            }}
            disabled={!previewEnabled()}
            rightIcon={<IconArrowRight />}
          >
            <Trans>Preview</Trans>
          </ButtonPrimary>
        }
      />

      <section>
        <TemplatingInfoBar />

        <SenderSelect
          value={props.email_identity_id}
          onChange={(v) => props.onStateUpdate({ email_identity_id: v })}
        />

        <Label htmlFor={emailSubjectId}>
          <Trans>Email subject</Trans>
        </Label>
        <Input
          id={emailSubjectId}
          fullWidth
          value={props.emailSubject}
          onChange={(val) => {
            props.onStateUpdate({ emailSubject: val })
          }}
        />
      </section>

      <section>
        <Label htmlFor={emailBodyId}>
          <Trans>Cover image</Trans>{' '}
          <span className="optional">
            <Trans>optional</Trans>
          </span>
        </Label>
        <div
          className={classNames('ImageUpload', {
            'has-preview': props.coverImageBase64 || props.coverImageSrc
          })}
        >
          {props.coverImageBase64 && (
            <img src={props.coverImageBase64} alt={t`cover image`} />
          )}
          {props.coverImageSrc && (
            <img src={props.coverImageSrc} alt={t`cover image`} />
          )}
          {!props.coverImageBase64 && !props.coverImageSrc && <IconPicture />}
          <input
            title={t`Upload image`}
            type="file"
            onChange={onImageChange}
            ref={(input) => {
              inputFileRef = input
            }}
            accept="image/*"
          />
          {((props.coverImageBase64 || props.coverImageSrc) && (
            <ButtonGroup>
              <ButtonLight
                onClick={() => {
                  if (inputFileRef) {
                    inputFileRef.click()
                  }
                }}
              >
                <Trans>Change image</Trans>
              </ButtonLight>
              <ButtonDanger
                onClick={() => {
                  props.onStateUpdate({
                    coverImageFile: null,
                    coverImageBase64: null,
                    coverImageSrc: null
                  })
                  if (inputFileRef) {
                    inputFileRef.value = ''
                  }
                }}
              >
                <Trans>Remove image</Trans>
              </ButtonDanger>
            </ButtonGroup>
          )) || (
            <ButtonLight
              onClick={() => {
                if (inputFileRef) {
                  inputFileRef.click()
                }
              }}
            >
              <Trans>Upload an image</Trans>
            </ButtonLight>
          )}
        </div>
      </section>

      <section>
        <Label htmlFor={emailBodyId}>
          <Trans>Email body</Trans>
        </Label>
        <EditorQuill
          value={props.emailBody}
          uploadFn={async (fileName, blob) => {
            return LooopS3FileUpload({
              blob: blob,
              S3UploadProps: {
                filename: fileName,
                S3path: `campaigns/email-body-images`,
                bucket: LooopS3BucketEnum.PUBLIC
              }
            })
          }}
          onChange={async (content) => {
            const html = await replaceBase64ImagesInHtml(content, {
              S3path: `campaigns/email-body-images`,
              bucket: LooopS3BucketEnum.PUBLIC
            })

            props.onStateUpdate({
              emailBody: addInlineWidthAttributeToImages(html)
            })
          }}
        />
      </section>

      {!isManagerNotification && (
        <>
          <section>
            <Box>
              <Label>
                <Trans>Button</Trans>{' '}
                <Box as="span" color={brandColors.grey.light}>
                  <Trans>optional</Trans>{' '}
                  <TooltipHelpQuestion>
                    <Trans>
                      By not adding a button link and text, your campaign
                      effectiveness score will be impacted in campaign summary
                      and dashboards
                    </Trans>
                  </TooltipHelpQuestion>
                </Box>
              </Label>
            </Box>

            <Label htmlFor={buttonLinkId}>
              <Trans>Link</Trans>
            </Label>
            <Input
              id={buttonLinkId}
              fullWidth
              value={props.buttonLink}
              className={classNames({ invalid: !props.buttonLinkValid })}
              type="url"
              pattern="^(ftp:\/\/|http:\/\/|https:\/\/).*$"
              onChange={(val) => {
                props.onStateUpdate({
                  buttonLink: val,
                  buttonLinkValid: !!val.match(validations.url)
                })
              }}
            />
          </section>

          <section>
            <Label htmlFor={buttonTextId}>
              <Trans>Text</Trans>
            </Label>
            <Input
              id={buttonTextId}
              fullWidth
              value={props.buttonText}
              onChange={(val) => {
                props.onStateUpdate({ buttonText: val })
              }}
            />
          </section>
        </>
      )}

      <section className="CampaignEmailComponent__footer">
        <ButtonPrimary
          disabled={!previewEnabled()}
          onClick={() => {
            props.onStateUpdate({
              step: CampaignActionEditingSteps.PREVIEW
            })
          }}
        >
          <Trans>Preview message</Trans>
        </ButtonPrimary>
      </section>
    </div>
  )
}
